/**
 * Navigation state machine - replaces useNavigationStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { ViewPermission } from '@repo/api-client'
import { buildNavigationFromPermissions } from '@/shared/lib/navigation-build'
import { navigationDefinitions } from '@/stores/navigation-store'
import type { NavigationItem } from '@/stores/navigation-store'

export interface NavigationContext {
  viewPermissions: ViewPermission[]
  navigation: NavigationItem[]
  activeMenus: NavigationItem[]
  isLoading: boolean
  isInitialized: boolean
}

export type NavigationEvent =
  | { type: 'SET_VIEW_PERMISSIONS'; permissions: ViewPermission[] }
  | { type: 'BUILD_NAVIGATION'; rootViewName: string }
  | { type: 'SET_ACTIVE_MENUS'; items: NavigationItem[] }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_INITIALIZED'; initialized: boolean }
  | { type: 'RESET' }

const machineSetup = setup({
  types: {
    context: {} as NavigationContext,
    events: {} as NavigationEvent,
  },
})

export const navigationMachine = machineSetup.createMachine({
  id: 'navigation',
  context: {
    viewPermissions: [],
    navigation: [],
    activeMenus: [],
    isLoading: false,
    isInitialized: false,
  },
  on: {
    SET_VIEW_PERMISSIONS: {
      actions: assign({
        viewPermissions: ({ event }) =>
          event.type === 'SET_VIEW_PERMISSIONS' ? event.permissions : [],
      }),
    },
    BUILD_NAVIGATION: {
      actions: assign({
        navigation: ({ context, event }) =>
          event.type === 'BUILD_NAVIGATION'
            ? (buildNavigationFromPermissions(
                context.viewPermissions,
                event.rootViewName,
                (name) => (navigationDefinitions[name] || {}) as any
              ) as NavigationItem[])
            : context.navigation,
      }),
    },
    SET_ACTIVE_MENUS: {
      actions: assign({
        activeMenus: ({ event }) => (event.type === 'SET_ACTIVE_MENUS' ? event.items : []),
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
    SET_INITIALIZED: {
      actions: assign({
        isInitialized: ({ event }) =>
          event.type === 'SET_INITIALIZED' ? event.initialized : false,
      }),
    },
    RESET: {
      actions: assign({
        viewPermissions: [],
        navigation: [],
        activeMenus: [],
        isLoading: false,
        isInitialized: false,
      }),
    },
  },
})
